import React, { ChangeEvent, ChangeEventHandler, Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button } from '@mui/material';

import { extractUploadedFilesFromZip, FileFromBrowserUpload, UploadedFile } from '@paragon/component-processor';

import { compareFiles } from '../../actions/compareFiles';
import fileTypes from '../../data/fileTypes';

const fileTypesAcceptedForComparison = [...fileTypes.mitek.fileExtensions, ...fileTypes.simpson.fileExtensions, 'zip'];
const fileTypesToAcceptForComparisonHtmlAccept = fileTypesAcceptedForComparison
  .map((fileType) => `.${fileType}`)
  .join(',');
function getFilesOfTypesAcceptedForComparison(files: readonly UploadedFile[]) {
  return files.filter((file) =>
    fileTypesAcceptedForComparison.map((fileType) => fileType.toLowerCase()).includes(file.filetype!.toLowerCase()),
  );
}

function resetEventTargetValue(event: React.MouseEvent<HTMLInputElement>) {
  (event.target as any).value = null;
}

type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = DispatchProps;

interface State {
  readonly compareSetOneFileData: readonly UploadedFile[] | null;
  readonly compareSetOneNumberOfFiles: number;
  readonly compareSetTwoFileData: readonly UploadedFile[] | null;
  readonly compareSetTwoNumberOfFiles: number;
  readonly inputErrorText: string | null;
}

class CompareControls extends Component<Props, State> {
  state: State = {
    compareSetOneFileData: null,
    compareSetOneNumberOfFiles: 0,
    compareSetTwoFileData: null,
    compareSetTwoNumberOfFiles: 0,
    inputErrorText: null,
  };

  onSetFileSetOne: ChangeEventHandler<HTMLInputElement> = (event) => {
    this.onSetFileSet(event, 'compareSetOne');
  };

  onSetFileSetTwo: ChangeEventHandler<HTMLInputElement> = (event) => {
    this.onSetFileSet(event, 'compareSetTwo');
  };

  onSetFileSet = async (
    event: ChangeEvent<HTMLInputElement>,
    statePropertyPrefix: 'compareSetOne' | 'compareSetTwo',
  ) => {
    const parsedFiles = [...event.target.files!].map((file) => new FileFromBrowserUpload(file));
    const filesOfFileTypesAcceptedForUpload = getFilesOfTypesAcceptedForComparison(parsedFiles);
    const zipFiles = filesOfFileTypesAcceptedForUpload.filter((file) => file.filetype!.toLowerCase() === 'zip');
    let numberOfFiles = filesOfFileTypesAcceptedForUpload.length - zipFiles.length;
    await Promise.all(
      zipFiles.map(async (zipFile) => {
        await zipFile.getContents();
        const filesInZipFile = await extractUploadedFilesFromZip(zipFile.contents!);
        const filesInZipFileAcceptedForUpload = getFilesOfTypesAcceptedForComparison(filesInZipFile);
        numberOfFiles += filesInZipFileAcceptedForUpload.length;
      }),
    );

    this.setState({
      [`${statePropertyPrefix}FileData`]: filesOfFileTypesAcceptedForUpload,
      [`${statePropertyPrefix}NumberOfFiles`]: numberOfFiles,
    } as any);
  };

  onCompare = () => {
    const inputErrorText = [];
    if (this.state.compareSetOneFileData == null) {
      inputErrorText.push('Select file(s) for File Set 1.');
    }
    if (this.state.compareSetTwoFileData == null) {
      inputErrorText.push('Select file(s) for File Set 2.');
    }
    if (inputErrorText.length > 0) {
      this.setState({ inputErrorText: inputErrorText.join(' ') });
      return;
    }

    this.props.compareFiles(this.state.compareSetOneFileData!, this.state.compareSetTwoFileData!);

    this.setState({
      compareSetOneFileData: null,
      compareSetTwoFileData: null,
      inputErrorText: null,
    });
  };

  render() {
    let fileSetOneButtonText = 'Select File Set 1';
    if (this.state.compareSetOneFileData != null) {
      if (this.state.compareSetOneNumberOfFiles === 1) fileSetOneButtonText = '1 file';
      else fileSetOneButtonText = `${this.state.compareSetOneNumberOfFiles} files`;
    }

    let fileSetTwoButtonText = 'Select File Set 2';
    if (this.state.compareSetTwoFileData != null) {
      if (this.state.compareSetTwoNumberOfFiles === 1) fileSetTwoButtonText = '1 file';
      else fileSetTwoButtonText = `${this.state.compareSetTwoNumberOfFiles} files`;
    }

    return (
      <>
        <Box>{this.state.inputErrorText}</Box>
        <Box sx={{ display: 'flex', my: 2 }}>
          <Box sx={{ flex: 1, mx: 5 }}>
            <input
              id="upload-input-1"
              type="file"
              accept={fileTypesToAcceptForComparisonHtmlAccept}
              multiple
              onChange={this.onSetFileSetOne}
              onClick={resetEventTargetValue}
              hidden
            />
            <label htmlFor="upload-input-1">
              <Button variant="outlined" fullWidth component="span">
                {fileSetOneButtonText}
              </Button>
            </label>
          </Box>
          <Box sx={{ flex: 1, mx: 5 }}>
            <input
              id="upload-input-2"
              type="file"
              accept={fileTypesToAcceptForComparisonHtmlAccept}
              multiple
              onChange={this.onSetFileSetTwo}
              onClick={resetEventTargetValue}
              hidden
            />
            <label htmlFor="upload-input-2">
              <Button variant="outlined" fullWidth component="span">
                {fileSetTwoButtonText}
              </Button>
            </label>
          </Box>
          <Box sx={{ flex: 1, mx: 5 }}>
            <Button variant="outlined" fullWidth onClick={this.onCompare}>
              Compare
            </Button>
          </Box>
        </Box>
      </>
    );
  }
}

const actionCreators = {
  compareFiles,
};

export default connect(null, actionCreators)(CompareControls);
